import type { Variants, Transition } from 'framer-motion'

/**
 * Check if user prefers reduced motion
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Default spring transition for smooth animations
 */
export const springTransition: Transition = {
  type: 'spring',
  stiffness: 300,
  damping: 30,
}

/**
 * Gentle spring for larger elements
 */
export const gentleSpring: Transition = {
  type: 'spring',
  stiffness: 100,
  damping: 20,
}

/**
 * Fade in animation variant
 */
export const fadeIn: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.4 },
  },
}

/**
 * Fade in with delay
 */
export const fadeInWithDelay = (delay: number): Variants => ({
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.4, delay },
  },
})

/**
 * Slide up animation variant
 */
export const slideUp: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4 },
  },
}

/**
 * Slide variants for wizard/carousel transitions
 * Supports directional sliding (left/right) based on custom prop
 */
export const slideVariants: Variants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 100 : -100,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: { duration: 0.3 },
  },
  exit: (direction: number) => ({
    x: direction < 0 ? 100 : -100,
    opacity: 0,
    transition: { duration: 0.3 },
  }),
}

/**
 * Slide up with stagger for list items
 */
export const slideUpStagger: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
}

export const slideUpStaggerItem: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4 },
  },
}

/**
 * Scale spring animation (for modals, dialogs)
 */
export const scaleSpring: Variants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: springTransition,
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: { duration: 0.2 },
  },
}

/**
 * Card hover effect
 */
export const cardHover: Variants = {
  rest: {
    y: 0,
    boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
  },
  hover: {
    y: -4,
    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
    transition: springTransition,
  },
}

/**
 * Pulse animation for capacity warnings
 */
export const pulseWarning: Variants = {
  rest: { scale: 1 },
  pulse: {
    scale: [1, 1.05, 1],
    transition: {
      duration: 1.5,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
}

/**
 * Floating animation for hero aerial silhouette
 */
export const floatingAnimation: Variants = {
  animate: {
    y: [0, -10, 0],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
}

/**
 * Confetti burst animation for enrollment success
 */
export const confettiBurst = {
  hidden: { opacity: 0, scale: 0, rotate: 0 },
  visible: (i: number) => ({
    opacity: [0, 1, 1, 0],
    scale: [0, 1, 1, 0.5],
    rotate: [0, 360],
    x: [0, (Math.random() - 0.5) * 200],
    y: [0, (Math.random() - 0.5) * 200],
    transition: {
      duration: 1.2,
      delay: i * 0.05,
      ease: 'easeOut',
    },
  }),
}

/**
 * Stagger container for multiple children
 */
export const staggerContainer: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
}

/**
 * Page transition variants
 */
export const pageTransition: Variants = {
  initial: { opacity: 0, y: 10 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3 },
  },
  exit: {
    opacity: 0,
    y: -10,
    transition: { duration: 0.2 },
  },
}

/**
 * Count-up animation for numbers
 * Use with framer-motion's useMotionValue and animate
 */
export const countUpTransition: Transition = {
  duration: 1,
  ease: 'easeOut',
}

/**
 * Drawer slide in from bottom (mobile)
 */
export const drawerSlideUp: Variants = {
  hidden: { y: '100%', opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: gentleSpring,
  },
  exit: {
    y: '100%',
    opacity: 0,
    transition: { duration: 0.2 },
  },
}

/**
 * Modal backdrop fade
 */
export const backdropFade: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.2 },
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.2 },
  },
}

/**
 * Helper: Create animation variants that respect reduced motion preference
 */
export function withReducedMotion<T extends Variants>(variants: T): T {
  if (prefersReducedMotion()) {
    // Return simplified variants with no motion, just opacity
    return Object.keys(variants).reduce(
      (acc, key) => ({
        ...acc,
        [key]: { opacity: key === 'hidden' ? 0 : 1 },
      }),
      {} as T
    )
  }
  return variants
}
